import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type {
  ScenarioCaseResult,
  ScenarioRun,
  ScenarioRunStatus,
} from '@/lib/api/scenario-runs.api';

const STATUS_VARIANT: Record<
  ScenarioRunStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  QUEUED: 'outline',
  RUNNING: 'secondary',
  JUDGED: 'default',
  FAILED: 'destructive',
  CANCELLED: 'outline',
};

export const ScenarioRunStatusBadge = ({ status }: { status: ScenarioRunStatus }) => (
  <Badge variant={STATUS_VARIANT[status] ?? 'outline'} className="gap-1">
    {status === 'RUNNING' && <Loader2 className="h-3 w-3 animate-spin" />}
    {status}
  </Badge>
);

const RESULT_CLASS: Record<ScenarioCaseResult, string> = {
  pass: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
  fail: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300',
  inconclusive:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
};

const PENDING_CLASS =
  'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300';

/** Coloured badge for a case: pass green, fail red, inconclusive amber, otherwise grey. */
export const CaseResultBadge = ({
  result,
  status,
  className,
}: {
  result: ScenarioCaseResult | null;
  status: string;
  className?: string;
}) => {
  const active = status === 'pending' || status === 'running' || status === 'judging';
  return (
    <Badge
      variant="outline"
      className={cn('gap-1 capitalize', result ? RESULT_CLASS[result] : PENDING_CLASS, className)}
    >
      {!result && active && <Loader2 className="h-3 w-3 animate-spin" />}
      {result ?? status}
    </Badge>
  );
};

export const RunCounts = ({
  run,
}: {
  run: Pick<ScenarioRun, 'passed' | 'failed' | 'inconclusive' | 'total'>;
}) => (
  <span className="flex items-center gap-2 text-xs whitespace-nowrap">
    <span className="text-emerald-600 dark:text-emerald-400">{run.passed} pass</span>
    <span className="text-rose-600 dark:text-rose-400">{run.failed} fail</span>
    <span className="text-amber-600 dark:text-amber-400">{run.inconclusive} inconclusive</span>
    <span className="text-muted-foreground">/ {run.total}</span>
  </span>
);

export const targetLabel = (run: Pick<ScenarioRun, 'target_kind' | 'commit_id' | 'branch_id'>) => {
  if (run.target_kind === 'live') return 'live';
  if (run.target_kind === 'branch') {
    return `branch ${run.commit_id ? run.commit_id.slice(0, 8) : (run.branch_id?.slice(0, 8) ?? '')}`;
  }
  return `commit ${run.commit_id?.slice(0, 8) ?? ''}`;
};
